<script lang="ts" setup generic="T">
import debounce from '~~/composables/debounce';
import MinusIcon from '~/assets/icons/basket/16/minus.svg'
import PlusIcon from '~/assets/icons/basket/16/plus.svg'
import type { ICounterSettings } from '~~/shared/interfaces/ui/form/formInputs';

enum SetCountTypes {
  increment = 'increment',
  decrement = 'decrement',
}

const props = defineProps<{
  counterSettings: ICounterSettings<T>,
  maxLength?: number
  type?: 'white'
}>();

const emits = defineEmits(['update:modelValue', 'set-count']);

const count = ref(1);
const unavailableCount = ref(false);
const minCount = 0;

const handleInput = props.counterSettings.hasDebounce ? debounce(setCount, 200) : setCount;

onMounted(() => {
  setCountOnPropsChange();
})

watch(() => props.counterSettings.maxCount, () => {
  setCountOnPropsChange();
})

watch(() => props.counterSettings.count, () => {
  if (props.counterSettings.count != count.value) {
    count.value = props.counterSettings.count;
  }
})

const counterClasses = computed(() => ({
  [`counter_${props.type}`]: props.type,
}))

function setCountOnPropsChange() {
  count.value = props.counterSettings.count;
  if (props.counterSettings.maxCount && count.value > props.counterSettings.maxCount) {
    unavailableCount.value = true;
  }else {
    unavailableCount.value = false;
  }
}

function setCountOnButtonClick(type: SetCountTypes) {
  if (type == SetCountTypes.decrement) {
    count.value--;
  }else {
    count.value++;
  }
  setCount();
}

function setCount() {
  validateNewCount();
  sendNewCounterData();
}

function setCountValueAfterInput() {
  if (props.counterSettings.withoutSetOnInput && props.counterSettings.maxCount && count.value > props.counterSettings.maxCount) {
    count.value = props.counterSettings.maxCount;
    unavailableCount.value = false;
    sendNewCounterData();
  }
}

function validateNewCount() {
  if (Number.isNaN(Number(count.value))) {
    count.value = props.counterSettings.count;
  }

  count.value = Math.floor(count.value);

  if (count.value < minCount) {
    count.value = minCount;
  } else if (
    props.counterSettings.maxCount &&
    count.value > props.counterSettings.maxCount
  ) {
    if (props.counterSettings.withoutSetOnInput) {
      unavailableCount.value = true;
    }else {
      count.value = props.counterSettings.maxCount;
    }
  }else {
    unavailableCount.value = false;
  }
}

function sendNewCounterData() {
  if (props.counterSettings.counterItem) {
    emits('set-count', {
      counterItem: props.counterSettings.counterItem,
      count: count.value,
    });
  }else {
    emits('update:modelValue', count.value);
  }
}
</script>

<template>
  <div class="counter" :class="counterClasses">
    <button
      class="counter-btn"
      v-if="counterSettings.hasButtons"
      @click="setCountOnButtonClick(SetCountTypes.decrement)"
      :disabled="
        counterSettings.decrementDisabled ||
        counterSettings.isDisabled ||
        count == 1
      "
    >
      <MinusIcon/>
    </button>
    <input
      v-model="count"
      @input="handleInput"
      @focusout="setCountValueAfterInput"
      @keyup.enter="setCountValueAfterInput"
      type="text"
      :maxlength="maxLength"
      class="counter-input"
      :disabled="
        counterSettings.isDisabled ||
        (!counterSettings.disabledOnlyForZeroMaxCount && counterSettings.maxCount == count && count == 1) ||
        counterSettings.maxCount == 0
      "
      :class="[
        (unavailableCount && counterSettings.hasBgIndicator) && 'error',
        counterSettings.hasButtons && 'has_buttons'
      ]"
    />
    <button
      class="counter-btn"
      v-if="counterSettings.hasButtons"
      @click="setCountOnButtonClick(SetCountTypes.increment)"
      :disabled="
        counterSettings.incrementDisabled ||
        counterSettings.isDisabled ||
        (!counterSettings.disabledOnlyForZeroMaxCount && counterSettings.maxCount == count) ||
        counterSettings.maxCount == 0
      "
    >
      <PlusIcon/>
    </button>
  </div>
</template>

<style lang="scss" scoped>
.counter {
  display: flex;
  align-items: center;
  overflow: hidden;
  border-radius: 8px;

  &-input {
    width: 100%;
    height: 36px;
    color: $content_primary;
    background: $control-bg_neutral;
    text-align: center;
    transition: 0.2s background-color;
    @include font(16px, 20px, 500);

    //@include md-breakpoint {
    //  min-height: 56px;
    //  max-width: 56px;
    //}

    &:disabled {
      color: $content_tretiary;
    }

    &.error {
      background-color: $light-red;
    }

    &.has_buttons {
      width: 36px;
    }
  }
  &-btn {
    width: 36px;
    height: 36px;
    color: $control-content_primary;
    background-color: $control-bg_neutral;
    text-align: center;
    @include font(16px, 20px, 500);
    display: flex;
    align-items: center;
    justify-content: center;


    &:disabled {
      cursor: default;

      svg {
        :deep(path) {
          fill: $control-content_tretiary;
        }
      }
    }
    &:hover:not(:active, :disabled) {
      @include mf-breakpoint {
        background-color: $control-bg_neutral-hover;
      }
    }
  }

  &_white{
    .counter{
      &-input,
      &-btn{
        background-color: $bg_page;
      }
    }
  }
}
</style>
